import { useCallback, useEffect, useMemo, useState } from 'react';
import { loadConfig } from '@hotpot/core';
import type { Backend, Me, SiteHandle } from './backend/types.js';
import { ErrorBox, Loading } from './components/ui.js';
import { Layout } from './components/Layout.js';
import { browserLang, LangContext, translator, useT, type Lang } from './i18n.js';
import { match, RouterProvider, useRouter } from './router.js';
import { BackendContext, SessionContext, SiteProvider, ToastProvider, useBackend, useSession } from './state.js';
import { CollectionList } from './screens/CollectionList.js';
import { EntryEditor } from './screens/EntryEditor.js';
import { SiteHistory } from './screens/History.js';
import { Login, Verify } from './screens/Login.js';
import { Media } from './screens/Media.js';
import { People } from './screens/People.js';
import { SettingsIndex, SiteHome } from './screens/SiteHome.js';
import { Account, NotFound, Privacy, Sites } from './screens/Sites.js';

export function App({ backend }: { backend: Backend }) {
  const [lang, setLangState] = useState<Lang>(browserLang);
  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem('hp-lang', l);
    } catch {
      /* storage blocked: the choice lasts until reload */
    }
  }, []);
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  const langValue = useMemo(() => ({ lang, t: translator(lang), setLang }), [lang, setLang]);
  return (
    <LangContext.Provider value={langValue}>
      <BackendContext.Provider value={backend}>
        <RouterProvider>
          <ToastProvider>
            <Session>
              <Routes onSiteLang={(l) => {
                let chosen = false;
                try {
                  chosen = !!localStorage.getItem('hp-lang');
                } catch {
                  /* ignore */
                }
                if (!chosen && (l === 'cs' || l === 'en')) setLangState(l);
              }} />
            </Session>
            {backend.demo && <DemoBar />}
          </ToastProvider>
        </RouterProvider>
      </BackendContext.Provider>
    </LangContext.Provider>
  );
}

function Session({ children }: { children: React.ReactNode }) {
  const backend = useBackend();
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [error, setError] = useState<Error | null>(null);
  const refresh = useCallback(async () => {
    try {
      setMe(await backend.me());
    } catch (e) {
      setError(e as Error);
    }
  }, [backend]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const value = useMemo(() => ({ me: me ?? null, setMe, refresh }), [me, refresh]);
  if (error) return <div style={{ padding: 30 }}><ErrorBox error={error} onRetry={() => { setError(null); void refresh(); }} /></div>;
  if (me === undefined) return null;
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

const PUBLIC = ['/login', '/login/verify', '/privacy'];

function Routes({ onSiteLang }: { onSiteLang: (l: string) => void }) {
  const { me } = useSession();
  const { loc, navigate } = useRouter();
  const path = loc.path.replace(/\/+$/, '') || '/';

  useEffect(() => {
    if (!me && !PUBLIC.includes(path)) navigate('/login', { replace: true, force: true });
    if (me && path === '/login') navigate('/', { replace: true, force: true });
    if (me && path === '/' && me.sites.length === 1 && !me.user.studio) navigate(`/s/${me.sites[0]!.id}`, { replace: true, force: true });
  }, [me, path, navigate]);

  if (path === '/login/verify') return <Verify />;
  if (path === '/privacy') return <Privacy />;
  if (!me) return path === '/login' ? <Login /> : null;
  if (path === '/') return <Sites />;
  if (path === '/account') return <Account />;
  const s = path.match(/^\/s\/([^/]+)(\/.*)?$/);
  if (s) return <SiteRoute id={decodeURIComponent(s[1]!)} rest={s[2] ?? '/'} onSiteLang={onSiteLang} />;
  return <NotFound />;
}

function SiteRoute({ id, rest, onSiteLang }: { id: string; rest: string; onSiteLang: (l: string) => void }) {
  const backend = useBackend();
  const { loc } = useRouter();
  const [handle, setHandle] = useState<SiteHandle | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    let alive = true;
    setHandle(null);
    setError(null);
    backend.openSite(id).then(
      (h) => {
        if (!alive) return;
        try {
          const lang = loadConfig(h.configText).site.ui_language;
          if (lang) onSiteLang(lang);
        } catch {
          /* config errors show below */
        }
        setHandle(h);
      },
      (e) => alive && setError(e),
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backend, id, n]);

  if (error) {
    return (
      <Layout sidebar={false}>
        <ErrorBox error={error} onRetry={() => setN((x) => x + 1)} />
      </Layout>
    );
  }
  // The handle may still be the previous site's for one render after switching sites.
  if (!handle || handle.site.id !== id) {
    return (
      <Layout sidebar={false}>
        <Loading />
      </Layout>
    );
  }
  let page: React.ReactNode = <NotFound />;
  let m: Record<string, string> | null;
  if (rest === '/' || rest === '') page = <SiteHome />;
  else if ((m = match('/c/:c', rest))) page = <CollectionList name={m.c!} />;
  else if ((m = match('/c/:c/new', rest))) page = <EntryEditor key={`new-${m.c}-${loc.query.get('from') ?? ''}`} collection={m.c!} isNew from={loc.query.get('from') ?? undefined} />;
  else if ((m = match('/c/:c/e/:slug', rest))) page = <EntryEditor key={`${m.c}/${m.slug}`} collection={m.c!} slug={m.slug} />;
  else if ((m = match('/f/:c/:f', rest))) page = <EntryEditor key={`${m.c}/${m.f}`} collection={m.c!} file={m.f} />;
  else if (rest === '/media') page = <Media />;
  else if (rest === '/history') page = <SiteHistory />;
  else if (rest === '/people') page = <People />;
  else if (rest === '/settings') page = <SettingsIndex />;
  return (
    <SiteProvider key={id} handle={handle}>
      {page}
    </SiteProvider>
  );
}

function DemoBar() {
  const { lang } = useT();
  return (
    <div className="demo-bar" role="note">
      <strong>DEMO</strong>
      <span className="long">{lang === 'cs' ? 'Běží jen v prohlížeči, nic se neukládá na web. Obnovením stránky začnete znovu.' : 'Runs only in your browser; nothing reaches the real site. Reload to start over.'}</span>
      <button
        onClick={() => {
          try {
            sessionStorage.clear();
          } catch {
            /* ignore */
          }
          window.location.hash = '#/login';
          window.location.reload();
        }}
      >
        {lang === 'cs' ? 'Začít znovu' : 'Start over'}
      </button>
    </div>
  );
}

